import type {
  BalanceDay,
  ControllerInfo,
  DashboardState,
  DaySchedule,
  FeedEntry,
  ForecastCell,
  Kpi,
  PriceInfo,
  PumpInfo,
  SkipRules,
  WeatherNow,
  Zone,
} from "../types";
import type { IrrigationApi } from "./client";
import { priceLevelOf } from "../lib/price";
import { C } from "../theme";

const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));
const hhmm = () => new Date().toTimeString().slice(0, 5);

/**
 * Real Falsterbo system: zones 1–5 cool-season lawn (spray), zone 6 hydrangea
 * (drip, high demand), zone 7 rose (drip). Moisture is expressed as % of
 * plant-available water (100 = field capacity, refill at the MAD line).
 */
const seedZones = (): Zone[] => [
  { id: "z1", name: "Lawn — West", type: "spray", plant: "Lawn turf", area: 120, moisture: 58, band: [60, 100], mad: 60, fc: 100, state: "price-hold", enabled: true, dur: 14, flow: 0, nextIn: "Cheaper slot", nextReason: "Power very expensive · 1.85 kr/kWh, waiting for a cheaper slot", lastRun: "Yesterday 05:00", start: "05:00" },
  { id: "z2", name: "Lawn — East", type: "spray", plant: "Lawn turf", area: 90, moisture: 73, band: [60, 100], mad: 60, fc: 100, state: "idle", enabled: true, dur: 12, flow: 0, nextIn: "05:15", nextReason: "Holding above refill", lastRun: "Yesterday 05:15", start: "05:15" },
  { id: "z3", name: "Lawn — South", type: "spray", plant: "Lawn turf", area: 80, moisture: 64, band: [60, 100], mad: 60, fc: 100, state: "wind-hold", enabled: true, dur: 12, flow: 0, nextIn: "Wind < 4.5 m/s", nextReason: "Gust over sprinkler limit", lastRun: "Today 05:30", start: "05:30" },
  { id: "z4", name: "Lawn — North", type: "spray", plant: "Lawn turf", area: 60, moisture: 55, band: [60, 100], mad: 60, fc: 100, state: "idle", enabled: true, dur: 13, flow: 0, nextIn: "1h 40m", nextReason: "Below refill point", lastRun: "Yesterday 05:45", start: "05:45" },
  { id: "z5", name: "Lawn — Terrace", type: "spray", plant: "Lawn turf", area: 30, moisture: 41, band: [60, 100], mad: 60, fc: 100, state: "off", enabled: false, dur: 10, flow: 0, nextIn: "—", nextReason: "Disabled after no-flow alert", lastRun: "Today 06:00", start: "06:00" },
  { id: "z6", name: "Hydrangea bed", type: "drip", plant: "Hydrangea", area: 18, moisture: 62, band: [65, 100], mad: 65, fc: 100, state: "watering", enabled: true, dur: 35, remaining: 1290, total: 2100, flow: 4.2, nextIn: "", nextReason: "", lastRun: "Now", start: "06:20" },
  { id: "z7", name: "Rose bed", type: "drip", plant: "Rose", area: 14, moisture: 80, band: [60, 100], mad: 60, fc: 100, state: "idle", enabled: true, dur: 30, flow: 0, nextIn: "4h 20m", nextReason: "Topping up to field capacity", lastRun: "Today 05:30", start: "06:40" },
];

const WEATHER: WeatherNow = { tempC: 17, wind: 3.2, humidity: 64, rainNow: 0, et0: 4.1, rainToday: 0.6, cond: "Sunny" };

const FORECAST: ForecastCell[] = [
  { t: "18:00", icon: "sun", tempC: 17, rainMm: 0, wind: 3.1 },
  { t: "22:00", icon: "cloud-sun", tempC: 13, rainMm: 0, wind: 2.4 },
  { t: "02:00", icon: "rain", tempC: 10, rainMm: 4, wind: 2.0 },
  { t: "06:00", icon: "rain", tempC: 9, rainMm: 1, wind: 2.6 },
  { t: "10:00", icon: "cloud-sun", tempC: 14, rainMm: 0, wind: 3.4 },
  { t: "14:00", icon: "sun", tempC: 18, rainMm: 0, wind: 3.8 },
];

const BALANCE: BalanceDay[] = [
  { d: "Mon", et: 3.6, rain: 0, applied: 3.8 },
  { d: "Tue", et: 4.1, rain: 0, applied: 4.3 },
  { d: "Wed", et: 2.9, rain: 5.2, applied: 0 },
  { d: "Thu", et: 4.4, rain: 0, applied: 4.6 },
  { d: "Fri", et: 3.9, rain: 1.1, applied: 2.9 },
  { d: "Sat", et: 4.6, rain: 0, applied: 4.8 },
  { d: "Sun", et: 4.1, rain: 0.6, applied: 3.4 },
];

const RULES: SkipRules = { rainMm: 3, freezeC: 2, windMs: 4.5 };

// A textbook SE4 summer day (kr/kWh): cheap dawn, a deep midday solar trough,
// and a sharp evening peak. "Now" sits on the 08:00 morning ramp → very expensive.
const PRICE_CURVE = [
  0.72, 0.65, 0.61, 0.58, 0.55, 0.6, 0.78, 1.25, 1.85, 1.4, 0.9, 0.6,
  0.32, 0.28, 0.3, 0.45, 0.7, 1.1, 1.7, 2.15, 1.95, 1.5, 1.05, 0.82,
];
const PRICE_MEAN = 0.95;
const PRICE: PriceInfo = {
  enabled: true,
  ok: true,
  level: "VERY_EXPENSIVE",
  now: 1.85,
  mean: PRICE_MEAN,
  unit: "kr/kWh",
  series: PRICE_CURVE.map((v, h) => ({ h, v, level: priceLevelOf(v, PRICE_MEAN) })),
  nowHour: 8,
  nextCheapAt: "13:00",
  nextCheapPrice: 0.28,
  source: "Nord Pool SE4 · 15-min · demo",
  updatedMins: 6,
  windows: { spray: "04:00-09:00", drip: "00:00-16:00" },
  skip: { spray: "VERY_EXPENSIVE", drip: "EXPENSIVE" },
};

// Multi-day plan at 08:00 Fri (today + 2). Spray window is sunrise-anchored:
// summer sunrise ≈ 04:28, so the cycle must FINISH ~by sunrise → window ≈
// 04:00–06:45 (the 04:00 floor binds in midsummer). The lawn cluster is packed
// into the cheapest pre-dawn slots; rose drip is shifted to the 13:00 trough.
// Tomorrow's SE4 prices are a touch cheaper midday; Sunday's aren't published yet.
const SUNRISE_MIN = 4 * 60 + 28; // 04:28
const SPRAY_WIN: [number, number] = [240, 525]; // 04:00–08:45 — sized to fit all 5 lawn zones (≈225 min) + ~60 min cost buffer
const DRIP_WIN: [number, number] = [0, 960]; // 00:00–16:00 flexible — not sunrise-anchored
const TOMORROW_CURVE = [
  0.68, 0.6, 0.55, 0.52, 0.5, 0.56, 0.74, 1.1, 1.55, 1.2, 0.78, 0.5,
  0.26, 0.22, 0.25, 0.4, 0.66, 1.0, 1.5, 1.9, 1.7, 1.3, 0.95, 0.75,
];
const D = 1440;
const bandsFor = (curve: number[], day: number) =>
  curve.map((v, h) => ({ startMin: day * D + h * 60, durMin: 60, v, level: priceLevelOf(v, PRICE_MEAN) }));
const SCHEDULE: DaySchedule = {
  nowMin: 8 * 60,
  days: 3,
  dayLabels: ["Today", "Tomorrow", "Sun"],
  dayDates: ["Fri 26", "Sat 27", "Sun 28"],
  sunriseMin: SUNRISE_MIN,
  priceDays: [true, true, false],
  priceBands: [...bandsFor(PRICE_CURVE, 0), ...bandsFor(TOMORROW_CURVE, 1)],
  zones: [
    // West held past this morning's window (price) → next is tomorrow's cheapest pre-dawn slot.
    { id: "z1", name: "Lawn — West", type: "spray", window: SPRAY_WIN, nextRunMin: D + 276, nextRunLabel: "Tomorrow 04:36" },
    { id: "z2", name: "Lawn — East", type: "spray", window: SPRAY_WIN, nextRunMin: D + 240, nextRunLabel: "Tomorrow 04:00" },
    { id: "z3", name: "Lawn — South", type: "spray", window: SPRAY_WIN, nextRunMin: D + 264, nextRunLabel: "Tomorrow 04:24" },
    { id: "z4", name: "Lawn — North", type: "spray", window: SPRAY_WIN, nextRunMin: D + 252, nextRunLabel: "Tomorrow 04:12" },
    { id: "z5", name: "Lawn — Terrace", type: "spray", window: SPRAY_WIN, nextRunMin: null, nextRunLabel: "—" },
    { id: "z6", name: "Hydrangea bed", type: "drip", window: DRIP_WIN, nextRunMin: D + 780, nextRunLabel: "Tomorrow 13:00" },
    { id: "z7", name: "Rose bed", type: "drip", window: DRIP_WIN, nextRunMin: 780, nextRunLabel: "Today 13:00" },
  ],
  blocks: [
    // ── Today: dawn lawn cluster already ran inside the sunrise window; hydrangea watering now; rose at the cheap midday.
    { zoneId: "z2", startMin: 252, durMin: 12, kind: "done" }, // 04:12
    { zoneId: "z4", startMin: 276, durMin: 13, kind: "done" }, // 04:36
    { zoneId: "z3", startMin: 300, durMin: 12, kind: "done" }, // 05:00
    { zoneId: "z6", startMin: 467, durMin: 35, kind: "watering" }, // ~07:47, running now
    { zoneId: "z7", startMin: 780, durMin: 30, kind: "planned", level: "VERY_CHEAP", est: true }, // 13:00 solar trough
    // ── Tomorrow (projected): lawn packed into the cheapest pre-dawn slots, finishing by sunrise; drip on the cheap midday.
    { zoneId: "z2", startMin: D + 240, durMin: 12, kind: "planned", level: "VERY_CHEAP", est: true },
    { zoneId: "z4", startMin: D + 252, durMin: 13, kind: "planned", level: "VERY_CHEAP", est: true },
    { zoneId: "z3", startMin: D + 264, durMin: 12, kind: "planned", level: "VERY_CHEAP", est: true },
    { zoneId: "z1", startMin: D + 276, durMin: 14, kind: "planned", level: "VERY_CHEAP", est: true },
    { zoneId: "z6", startMin: D + 780, durMin: 34, kind: "planned", level: "VERY_CHEAP", est: true }, // 13:00 trough
    // ── Sunday (projected, no prices yet): dawn lawn cluster + hydrangea, earliest-feasible.
    { zoneId: "z2", startMin: 2 * D + 240, durMin: 12, kind: "planned", est: true },
    { zoneId: "z4", startMin: 2 * D + 252, durMin: 13, kind: "planned", est: true },
    { zoneId: "z3", startMin: 2 * D + 264, durMin: 12, kind: "planned", est: true },
    { zoneId: "z1", startMin: 2 * D + 276, durMin: 14, kind: "planned", est: true },
    { zoneId: "z6", startMin: 2 * D + 300, durMin: 34, kind: "planned", est: true },
  ],
};

const CONTROLLER: ControllerInfo = {
  online: true,
  board: "Waveshare ESP32-S3-ETH-8DI-8RO",
  network: "Ethernet · W5500",
  outputs: "7 valves + pump · 8RO",
  sensors: "RS485 soil ×3 + flow + air",
};

const SEED_FEED: FeedEntry[] = [
  { id: 1, time: "06:24", kind: "run", zone: "Hydrangea bed", text: "Watering 35 min · drip · refilling to field capacity" },
  { id: 2, time: "06:05", kind: "skip", zone: "Lawn — South", text: "Held — gust 5.1 m/s above the 4.5 m/s sprinkler limit" },
  { id: 3, time: "05:40", kind: "plan", zone: null, text: "Rain expected 02:00 (4 mm) — tonight's lawn cycle deferred" },
  { id: 4, time: "05:02", kind: "sync", zone: null, text: "Weather synced · 4.1 mm evapotranspiration (ET₀) demand today, 0.6 mm rain" },
  { id: 5, time: "06:00", kind: "alert", zone: "Lawn — Terrace", text: "No-flow on scheduled run — zone disabled, check valve & wiring" },
  { id: 6, time: "05:18", kind: "run", zone: "Lawn — East", text: "Watered 12 min · 64 L applied" },
  { id: 7, time: "05:00", kind: "run", zone: "Lawn — West", text: "Watered 14 min · sprinkler · 78 L applied" },
];

function kpis(zones: Zone[]): Kpi[] {
  const alerts = zones.filter((z) => z.state === "off" && !z.enabled).length;
  return [
    { label: "Water used today", value: 214, unit: "L", sub: "across 4 zones" },
    { label: "Saved vs fixed schedule", value: 38, unit: "%", sub: "rain + ET aware", accent: C.sageDeep },
    { label: "This week", value: "1,340", unit: "L", sub: "7-day total" },
    {
      label: "Open alerts",
      value: alerts,
      unit: "",
      sub: alerts ? "Lawn — Terrace · no-flow" : "All clear",
      accent: alerts ? C.coral : C.sageDeep,
    },
  ];
}

/** In-memory controller simulation, mirroring the prototype's 1s tick. */
export class MockApi implements IrrigationApi {
  private zones = seedZones();
  private feed = [...SEED_FEED];
  private paused = false;
  private syncedMinsAgo = 2;
  private feedId = 100;
  // Pump/pressure mirror of the worker model — advanced from the zone flow so the
  // demo's pump stays consistent with its valves (no separate fabricated numbers).
  private pump = { pressure: 4.55, on: false, clog: 0.34, cycles: 14, runSec: 2280, cutIn: 2.8, cutOut: 4.6 };

  constructor() {
    setInterval(() => this.tick(), 1000);
    setInterval(() => (this.syncedMinsAgo += 1), 60_000);
  }

  private addFeed(e: Omit<FeedEntry, "id">) {
    this.feed = [{ id: ++this.feedId, ...e }, ...this.feed].slice(0, 60);
  }

  private tickPump() {
    const demand = this.zones.filter((z) => z.state === "watering").reduce((a, z) => a + z.flow, 0);
    const p = this.pump;
    if (demand <= 0.05) {
      p.on = false;
      p.pressure = p.cutOut;
    } else {
      const wasOn = p.on;
      if (p.pressure <= p.cutIn) p.on = true;
      if (p.pressure >= p.cutOut) p.on = false;
      const net = (p.on ? 92 : 0) - demand;
      p.pressure = clamp(p.pressure + (net / 60) * 0.16, p.cutIn - 0.5, p.cutOut + 0.05);
      if (p.on && !wasOn) p.cycles += 1;
      if (p.on) p.runSec += 1;
      p.clog = clamp(p.clog + 0.00002, 0, 0.97);
    }
  }

  private tick() {
    this.tickPump();
    let promote = false;
    this.zones = this.zones.map((z) => {
      if (z.state === "watering" && z.remaining != null) {
        const rem = z.remaining - 1;
        if (rem <= 0) {
          promote = true;
          const applied = Math.round(z.dur * (z.flow || 3.5));
          this.addFeed({ time: hhmm(), kind: "run", zone: z.name, text: `Finished ${z.dur} min · ${applied} L applied` });
          return { ...z, state: "idle", remaining: 0, flow: 0, moisture: clamp(z.moisture + 18, 0, z.fc), nextReason: "Topped up to field capacity", nextIn: "scheduled" };
        }
        return { ...z, remaining: rem, flow: +(z.flow + (Math.random() - 0.5) * 0.15).toFixed(1) };
      }
      if (z.state === "idle" && z.enabled && Math.random() < 0.05) {
        return { ...z, moisture: clamp(z.moisture - 0.4, 8, 100) };
      }
      return z;
    });
    if (promote) {
      const qi = this.zones.findIndex((z) => z.state === "queued");
      if (qi >= 0) {
        const q = this.zones[qi];
        this.zones[qi] = { ...q, state: "watering", total: q.dur * 60, remaining: q.dur * 60, flow: q.type === "drip" ? 4.2 : 9.4 };
      }
    }
  }

  async getState(): Promise<DashboardState> {
    const demand = this.zones.filter((z) => z.state === "watering").reduce((a, z) => a + z.flow, 0);
    const dp = Math.max(0, (this.pump.clog + 0.12) * (0.35 + 0.65 * (demand / 40)));
    const pump: PumpInfo = {
      on: this.pump.on,
      pressure: +this.pump.pressure.toFixed(2),
      p1: +this.pump.pressure.toFixed(2),
      p2: +Math.max(0, this.pump.pressure - dp).toFixed(2),
      dp: +dp.toFixed(2),
      flow: +demand.toFixed(1),
      clog: +this.pump.clog.toFixed(3),
      cutIn: this.pump.cutIn,
      cutOut: this.pump.cutOut,
      cyclesToday: this.pump.cycles,
      runSecToday: Math.round(this.pump.runSec),
      model: "Metabo HWW 6000/25 Inox",
      tankL: 24,
      live: false,
    };
    return {
      zones: this.zones.map((z) => ({ ...z })),
      feed: [...this.feed],
      weather: WEATHER,
      forecast: FORECAST,
      balance: BALANCE,
      kpis: kpis(this.zones),
      paused: this.paused,
      syncedMinsAgo: this.syncedMinsAgo,
      controller: CONTROLLER,
      rules: RULES,
      price: PRICE,
      pump,
      schedule: SCHEDULE,
      sources: {
        weather: "Open-Meteo + SMHI · live",
        soil: "Model (ET-driven) · simulated",
        valves: "Simulated",
        flow: "Simulated",
        power: "Nord Pool SE4 · demo",
        pump: "Model (valve-driven) · simulated",
      },
    };
  }

  async runZone(id: string, minutes: number) {
    const anyWatering = this.zones.some((z) => z.state === "watering");
    this.zones = this.zones.map((z) => {
      if (z.id !== id) return z;
      if (!z.enabled || z.state === "watering" || z.state === "queued") return z;
      if (anyWatering) {
        this.addFeed({ time: hhmm(), kind: "plan", zone: z.name, text: "Queued — one zone runs at a time to hold line pressure" });
        return { ...z, state: "queued", dur: minutes };
      }
      this.addFeed({ time: hhmm(), kind: "run", zone: z.name, text: `Watering ${minutes} min · ${z.type === "drip" ? "drip" : "sprinkler"} · manual run` });
      return { ...z, state: "watering", dur: minutes, total: minutes * 60, remaining: minutes * 60, flow: z.type === "drip" ? 4.2 : 9.4 };
    });
  }

  async stopZone(id: string) {
    this.zones = this.zones.map((z) => {
      if (z.id !== id || (z.state !== "watering" && z.state !== "queued")) return z;
      const wasWatering = z.state === "watering";
      this.addFeed({ time: hhmm(), kind: "plan", zone: z.name, text: wasWatering ? "Stopped — manual stop" : "Removed from queue — manual stop" });
      return { ...z, state: "idle", remaining: 0, flow: 0, nextReason: "Stopped manually", nextIn: "scheduled" };
    });
  }

  async setDuration(id: string, minutes: number) {
    this.zones = this.zones.map((z) => (z.id === id ? { ...z, dur: clamp(minutes, 1, 60) } : z));
  }

  async setEnabled(id: string, enabled: boolean) {
    this.zones = this.zones.map((z) =>
      z.id === id ? { ...z, enabled, state: enabled ? "idle" : "off", remaining: 0, flow: 0 } : z,
    );
  }

  async setRainDelay(paused: boolean) {
    this.paused = paused;
    this.addFeed({ time: hhmm(), kind: paused ? "plan" : "sync", zone: null, text: paused ? "Rain delay on — automatic watering paused" : "Rain delay cleared — automatic watering resumed" });
  }

  async sync() {
    await new Promise((r) => setTimeout(r, 700));
    this.syncedMinsAgo = 0;
    this.addFeed({ time: hhmm(), kind: "sync", zone: null, text: "Weather synced · 4.1 mm evapotranspiration (ET₀) demand today, 0.6 mm rain" });
  }
}
